import { AppLinkTree } from "@megarax/links";
import { motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom-v5-compat";

import { mobileNavCollapsedBarHeightPx } from "./navConstants";
import { getMobileDrawerSheetHeight } from "./navHelpers";
import { MobileNavCollapsedBar } from "./MobileNavCollapsedBar";
import { MobileNavDrawerSheet } from "./MobileNavDrawerSheet";
import { toMobileNavShortcuts } from "./mobileAppShortcuts";
import { useMobileNavDrag } from "./useMobileNavDrag";

type Props = {
  linkTree: AppLinkTree[];
  onSignOut: () => void;
  showProductionEnvAlert?: boolean;
};

export const MegaraxNavDrawer: React.FC<Props> = ({ linkTree, onSignOut, showProductionEnvAlert }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const collapsedBarRef = useRef<HTMLDivElement>(null);
  const [sheetHeight, setSheetHeight] = useState(getMobileDrawerSheetHeight);
  const drawer = useMobileNavDrag({ maxHeight: sheetHeight });
  const shortcuts = useMemo(
    () => toMobileNavShortcuts(linkTree, location.pathname, searchParams),
    [linkTree, location.pathname, searchParams],
  );

  useEffect(() => {
    const updateSheetHeight = () => {
      const collapsedBarHeight =
        collapsedBarRef.current?.offsetHeight ?? mobileNavCollapsedBarHeightPx;
      setSheetHeight(getMobileDrawerSheetHeight(collapsedBarHeight));
    };

    updateSheetHeight();
    window.visualViewport?.addEventListener("resize", updateSheetHeight);
    window.addEventListener("resize", updateSheetHeight);

    return () => {
      window.visualViewport?.removeEventListener("resize", updateSheetHeight);
      window.removeEventListener("resize", updateSheetHeight);
    };
  }, []);

  useEffect(() => {
    drawer.setIsExpanded(false);
  }, [location.pathname]);

  const isOpen = drawer.isExpanded || drawer.isDragging;

  return (
    <div
      css={{
        position: "fixed",
        bottom: 0,
        left: 0,
        width: "100%",
        zIndex: 1009,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
    >
      {isOpen && (
        <motion.div
          onClick={() => drawer.setIsExpanded(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.35 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          css={{ position: "fixed", inset: 0, backgroundColor: "black", zIndex: 1 }}
        />
      )}

      <div css={{ position: "relative", zIndex: 2 }}>
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            height: `${drawer.height}px`,
            overflow: "hidden",
            willChange: drawer.isDragging ? "height" : undefined,
            transition: drawer.isDragging ? undefined : "height 0.15s linear",
            background: "white",
            boxShadow: drawer.isExpanded ? "0 -8px 32px rgba(0,0,0,0.12)" : "none",
          }}
        >
          <MobileNavDrawerSheet
            linkTree={linkTree}
            onSignOut={onSignOut}
            showProductionEnvAlert={showProductionEnvAlert}
            onNavigate={() => drawer.setIsExpanded(false)}
          />
        </div>

        <div onTouchStart={drawer.onTouchStart} css={{ touchAction: "none" }}>
          <MobileNavCollapsedBar
            barRef={collapsedBarRef}
            shortcuts={shortcuts}
            isMenuOpen={drawer.isExpanded}
            onNavigate={() => drawer.setIsExpanded(false)}
            onMenuClick={() => drawer.setIsExpanded(!drawer.isExpanded)}
          />
        </div>
      </div>
    </div>
  );
};
